/**
 * The reception and the Study Gateway.
 *
 * Reception: four floating glass stations on an arc around the arrival point (01 Identity,
 * 02 Role, 03 Presence, 04 Study). The active card floats between them. A station is dim
 * until its step, bright while active and shows what was chosen once done. All four share
 * one canvas atlas (one draw call), redrawn only when a state changes; their floor rings
 * are one instanced mesh.
 *
 * Gateway: a standing ring of light at the north wall with a shimmering field inside, two
 * light pylons and a title above. The field takes the selected study's colour and grows
 * brighter when a study is chosen; walking through it enters the study.
 */
import * as THREE from 'three';
import { BODY_FONT, DISPLAY_FONT, GLASS, TOKENS } from '../../ui/theme.ts';
import { glowMaterial, type TimeUniform } from './glow.ts';

export const RECEPTION = {
  /** Where visitors arrive and stand for onboarding. */
  spawn: new THREE.Vector3(0, 0, 13.5),
  /** The active card (onboarding, then the study directory). */
  card: new THREE.Vector3(0, 1.45, 11.2),
  stationRadius: 3.4,
  stationAngles: [-76, -46, 46, 76],
};

export type StationState = 'pending' | 'active' | 'done';
export interface StationView {
  number: string;
  label: string;
  state: StationState;
  value?: string;
  colour?: string;
}

export interface Reception {
  group: THREE.Group;
  setStations(views: StationView[]): void;
  update(dt: number): void;
}

const COL_W = 384;
const COL_H = 640;

export function buildReception(): Reception {
  const group = new THREE.Group();
  group.name = 'reception';
  const n = RECEPTION.stationAngles.length;
  const canvas = document.createElement('canvas');
  canvas.width = COL_W * n;
  canvas.height = COL_H;
  const ctx = canvas.getContext('2d')!;
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  const slabW = 0.54;
  const slabH = slabW * (COL_H / COL_W);
  const parts: THREE.BufferGeometry[] = [];
  const ringMatrices: THREE.Matrix4[] = [];
  RECEPTION.stationAngles.forEach((deg, i) => {
    const a = (deg * Math.PI) / 180;
    const x = RECEPTION.spawn.x + Math.sin(a) * RECEPTION.stationRadius;
    const z = RECEPTION.spawn.z - Math.cos(a) * RECEPTION.stationRadius;
    const yaw = Math.atan2(RECEPTION.spawn.x - x, RECEPTION.spawn.z - z);
    const plane = new THREE.PlaneGeometry(slabW, slabH);
    const uv = plane.attributes.uv!;
    for (let k = 0; k < uv.count; k++) uv.setX(k, (i + uv.getX(k)) / n);
    plane.rotateX(-0.08);
    plane.rotateY(yaw);
    plane.translate(x, 1.62, z);
    parts.push(plane);
    ringMatrices.push(new THREE.Matrix4().makeTranslation(x, 0.02, z));
  });
  const merged = mergeAll(parts);
  const slabs = new THREE.Mesh(
    merged,
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      // Additive or see-through: one pass, not back faces then front faces.
      forceSinglePass: true,
      toneMapped: false,
      fog: false,
    }),
  );
  slabs.name = 'reception-stations';
  slabs.renderOrder = 6;
  group.add(slabs);
  const rings = new THREE.InstancedMesh(
    new THREE.TorusGeometry(0.34, 0.016, 6, 64).rotateX(Math.PI / 2),
    glowMaterial('#ffffff', 1),
    n,
  );
  ringMatrices.forEach((m, i) => rings.setMatrixAt(i, m));
  group.add(rings);
  const ringTargets = Array.from({ length: n }, () => new THREE.Color(TOKENS.steel));
  const ringNow = Array.from({ length: n }, () => new THREE.Color(TOKENS.steel));
  ringNow.forEach((c, i) => rings.setColorAt(i, c));

  let last = '';
  return {
    group,
    setStations: (views) => {
      const key = JSON.stringify(views);
      if (key === last) return;
      last = key;
      views.forEach((v, i) => {
        drawStation(ctx, i * COL_W, v);
        ringTargets[i]!.set(
          v.state === 'active'
            ? TOKENS.cyan
            : v.state === 'done'
              ? (v.colour ?? TOKENS.cyanSoft)
              : TOKENS.steel,
        );
        if (v.state === 'pending') ringTargets[i]!.multiplyScalar(0.6);
      });
      texture.needsUpdate = true;
    },
    update: (dt) => {
      let changed = false;
      ringNow.forEach((c, i) => {
        if (c.equals(ringTargets[i]!)) return;
        c.lerp(ringTargets[i]!, Math.min(1, dt * 3));
        rings.setColorAt(i, c);
        changed = true;
      });
      if (changed) rings.instanceColor!.needsUpdate = true;
    },
  };
}

function mergeAll(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const positions: number[] = [];
  const uvs: number[] = [];
  const index: number[] = [];
  for (const p of parts) {
    const base = positions.length / 3;
    positions.push(...(p.attributes.position!.array as Float32Array));
    uvs.push(...(p.attributes.uv!.array as Float32Array));
    for (const i of p.index!.array as Uint16Array) index.push(base + i);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setIndex(index);
  return g;
}

function drawStation(ctx: CanvasRenderingContext2D, x0: number, v: StationView): void {
  const w = COL_W;
  const h = COL_H;
  ctx.clearRect(x0, 0, w, h);
  const active = v.state === 'active';
  const done = v.state === 'done';
  ctx.save();
  ctx.translate(x0, 0);
  ctx.globalAlpha = v.state === 'pending' ? 0.55 : 1;
  const bg = ctx.createLinearGradient(0, 0, 0, h);
  bg.addColorStop(0, GLASS.top);
  bg.addColorStop(1, GLASS.bottom);
  ctx.fillStyle = bg;
  ctx.beginPath();
  ctx.roundRect(6, 6, w - 12, h - 12, 34);
  ctx.fill();
  ctx.strokeStyle = active ? 'rgba(63,214,255,0.85)' : GLASS.edge;
  ctx.lineWidth = active ? 4 : 2;
  ctx.stroke();
  // Hairline of light along the top.
  const hl = ctx.createLinearGradient(0, 0, w, 0);
  hl.addColorStop(0, 'rgba(63,214,255,0)');
  hl.addColorStop(0.5, active || done ? 'rgba(63,214,255,0.95)' : 'rgba(63,214,255,0.35)');
  hl.addColorStop(1, 'rgba(63,214,255,0)');
  ctx.fillStyle = hl;
  ctx.fillRect(40, 7, w - 80, 3);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = active ? TOKENS.cyan : done ? TOKENS.cyanSoft : TOKENS.silver;
  ctx.font = `600 120px ${DISPLAY_FONT}`;
  ctx.fillText(v.number, w / 2, 210);
  ctx.fillStyle = TOKENS.white;
  ctx.font = `600 46px ${DISPLAY_FONT}`;
  ctx.fillText(v.label, w / 2, 290);
  // State: a chip, then the chosen value.
  const chip = active ? 'NOW' : done ? 'DONE' : 'NEXT';
  ctx.font = `700 26px ${BODY_FONT}`;
  const cw = ctx.measureText(chip).width + 44;
  ctx.fillStyle = active
    ? 'rgba(63,214,255,0.18)'
    : done
      ? 'rgba(63,214,255,0.1)'
      : 'rgba(255,255,255,0.06)';
  ctx.beginPath();
  ctx.roundRect(w / 2 - cw / 2, 330, cw, 48, 24);
  ctx.fill();
  ctx.fillStyle = active ? TOKENS.cyan : done ? TOKENS.cyanSoft : TOKENS.silver;
  ctx.fillText(chip, w / 2, 364);
  if (v.value) {
    ctx.fillStyle = v.colour ?? TOKENS.mist;
    ctx.font = `500 36px ${BODY_FONT}`;
    wrapCentred(ctx, v.value, w / 2, 450, w - 60, 44, 3);
  }
  ctx.restore();
}

function wrapCentred(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  y: number,
  maxW: number,
  lineH: number,
  maxLines: number,
): void {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width > maxW && line) {
      lines.push(line);
      line = word;
    } else line = next;
  }
  if (line) lines.push(line);
  lines.slice(0, maxLines).forEach((l, i) => ctx.fillText(l, cx, y + i * lineH, maxW));
}

/* ---------------- Gateway ---------------- */

const WHITE = new THREE.Color('#ffffff');

export const GATEWAY = {
  centre: new THREE.Vector3(0, 0, -15),
  ringRadius: 2.3,
  /** Feet inside this box pass through the gateway. */
  zone: { minX: -1.7, maxX: 1.7, minZ: -15.7, maxZ: -14.3 },
  /** The study directory to the left of the gateway and the detail to the right. */
  directory: { x: -5.6, y: 2.05, z: -13.4 },
  detail: { x: 5.6, y: 2.05, z: -13.4 },
  /** Where a visitor stands to use the directory. */
  approach: new THREE.Vector3(0, 0, -10.5),
};

export interface Gateway {
  group: THREE.Group;
  /** The chosen study (code, title, colour), or undefined. */
  setStudy(study: { code: string; title: string; colour: string } | undefined): void;
  /** A flash as a visitor passes through. */
  flash(): void;
  update(dt: number): void;
}

export function buildGateway(time: TimeUniform): Gateway {
  const group = new THREE.Group();
  group.name = 'gateway';
  group.position.copy(GATEWAY.centre);
  const R = GATEWAY.ringRadius;
  const cy = R + 0.12;
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(R, 0.06, 10, 160),
    glowMaterial(TOKENS.cyanSoft, 0.6),
  );
  ring.position.y = cy;
  group.add(ring);
  const outer = new THREE.Mesh(
    new THREE.TorusGeometry(R + 0.22, 0.012, 6, 160),
    glowMaterial(TOKENS.cyan, 0.7),
  );
  outer.position.y = cy;
  group.add(outer);
  const fieldMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uTime: time,
      uColour: { value: new THREE.Color(TOKENS.cyan) },
      uActive: { value: 0 },
      uFlash: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform vec3 uColour;
      uniform float uActive;
      uniform float uFlash;
      varying vec2 vUv;
      void main() {
        vec2 p = vUv * 2.0 - 1.0;
        float r = length(p);
        float a = atan(p.y, p.x);
        float swirl = 0.5 + 0.5 * sin(a * 5.0 + r * 9.0 - uTime * (0.6 + uActive * 1.4));
        float rings = 0.5 + 0.5 * sin(r * 26.0 - uTime * 2.0);
        float edge = smoothstep(1.0, 0.82, r);
        float core = smoothstep(0.9, 0.0, r);
        float alpha = edge * (0.08 + 0.3 * swirl * core + 0.12 * rings * r + 0.25 * pow(r, 6.0)) * (0.45 + uActive * 1.1) + uFlash * edge * 0.9;
        gl_FragColor = vec4(mix(uColour, vec3(1.0), 0.15 + uFlash * 0.6) * alpha, alpha);
      }`,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
    // Additive or see-through: one pass, not back faces then front faces.
    forceSinglePass: true,
    toneMapped: false,
  });
  const field = new THREE.Mesh(new THREE.CircleGeometry(R - 0.05, 96), fieldMaterial);
  field.position.y = cy;
  group.add(field);
  const pylons = new THREE.InstancedMesh(
    new THREE.BoxGeometry(0.06, 5.2, 0.06).translate(0, 2.6, 0),
    glowMaterial(TOKENS.cyan, 0.85),
    2,
  );
  const d = new THREE.Object3D();
  [-1, 1].forEach((s, i) => {
    d.position.set(s * (R + 0.75), 0, 0);
    d.updateMatrix();
    pylons.setMatrixAt(i, d.matrix);
  });
  group.add(pylons);
  // Title above the ring.
  const titleCanvas = document.createElement('canvas');
  titleCanvas.width = 1536;
  titleCanvas.height = 256;
  const tctx = titleCanvas.getContext('2d')!;
  const titleTexture = new THREE.CanvasTexture(titleCanvas);
  titleTexture.colorSpace = THREE.SRGBColorSpace;
  const title = new THREE.Mesh(
    new THREE.PlaneGeometry(4.8, 0.8),
    new THREE.MeshBasicMaterial({
      map: titleTexture,
      transparent: true,
      depthWrite: false,
      toneMapped: false,
      fog: false,
    }),
  );
  title.position.set(0, cy + R + 0.85, 0);
  group.add(title);
  const drawTitle = (study: { code: string; title: string; colour: string } | undefined): void => {
    tctx.clearRect(0, 0, 1536, 256);
    tctx.textAlign = 'center';
    tctx.fillStyle = TOKENS.cyanSoft;
    tctx.font = `600 34px ${BODY_FONT}`;
    tctx.letterSpacing = '10px';
    tctx.fillText('STUDY GATEWAY', 768, 60);
    tctx.letterSpacing = '0px';
    tctx.fillStyle = study ? TOKENS.white : TOKENS.silver;
    tctx.font = `600 ${study ? 96 : 64}px ${DISPLAY_FONT}`;
    tctx.fillText(study ? study.code : 'Choose a study', 768, 160, 1480);
    if (study) {
      tctx.fillStyle = TOKENS.mist;
      tctx.font = `400 40px ${BODY_FONT}`;
      tctx.fillText(study.title, 768, 222, 1480);
    }
    titleTexture.needsUpdate = true;
  };
  drawTitle(undefined);
  const target = new THREE.Color(TOKENS.cyan);
  let active = 0;
  let activeTarget = 0;
  let flash = 0;
  return {
    group,
    setStudy: (study) => {
      target.set(study?.colour ?? TOKENS.cyan);
      activeTarget = study ? 1 : 0;
      drawTitle(study);
    },
    flash: () => {
      flash = 1;
    },
    update: (dt) => {
      active += (activeTarget - active) * Math.min(1, dt * 2.5);
      flash = Math.max(0, flash - dt * 1.6);
      fieldMaterial.uniforms.uActive!.value = active;
      fieldMaterial.uniforms.uFlash!.value = flash;
      (fieldMaterial.uniforms.uColour!.value as THREE.Color).lerp(target, Math.min(1, dt * 2.5));
      (ring.material as THREE.MeshBasicMaterial).color
        .copy(fieldMaterial.uniforms.uColour!.value)
        .lerp(WHITE, 0.05);
      (outer.material as THREE.MeshBasicMaterial).color.copy(fieldMaterial.uniforms.uColour!.value);
      (pylons.material as THREE.MeshBasicMaterial).color.copy(
        fieldMaterial.uniforms.uColour!.value,
      );
      ring.rotation.z += dt * 0.05;
      outer.rotation.z -= dt * 0.08;
    },
  };
}
